import { Link } from 'react-router-dom';
import { cartCount, cartSubtotal, useCart } from './store';
import { CartCrossSell } from './CartCrossSell';
import { useCartEditor } from './useCartEditor';
import { OptimizedImage } from '../../components/OptimizedImage';
import { Modal } from '../../components/ui/Modal';
import { formatKES } from '../../lib/format';

/** §4.5: an accessible drawer with a scrolling item area and persistent totals/actions. */
export function CartDrawer(): JSX.Element | null {
  const { isOpen, close, items, setQuantity, remove, add } = useCart();
  const subtotal = useCart(cartSubtotal);
  const count = useCart(cartCount);
  const { editing, edit } = useCartEditor();
  const unavailable = items.some((line) => line.quantity < 1 || line.quantity > line.maxAvailable);
  const blocked = editing || unavailable;
  return (
    <Modal
      open={isOpen}
      onClose={close}
      label="Shopping cart"
      presentation="drawer"
      className="flex h-full w-full max-w-full flex-col bg-white shadow-hover sm:w-[28rem]"
    >
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-frost px-5 py-4">
        <div>
          <h2 className="text-xl font-light text-navy-900">Your cart</h2>
          <p className="mt-1 text-xs text-neutral" role="status">
            {count} {count === 1 ? 'item' : 'items'}
            {editing ? ' · Updating…' : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={close}
          className="flex h-11 w-11 items-center justify-center rounded-full text-navy-700 hover:bg-cloud"
          aria-label="Close cart"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            aria-hidden
          >
            <path d="m6 6 12 12M18 6 6 18" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      {!items.length ? (
        <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center">
          <p className="text-lg font-light text-navy-900">Your cart is empty</p>
          <p className="mt-2 text-sm text-neutral">Find something for your next setup.</p>
          <Link
            to="/products"
            onClick={close}
            className="mt-5 inline-flex min-h-11 items-center rounded-lg bg-sky-600 px-5 text-sm font-medium text-white"
          >
            Browse products →
          </Link>
        </div>
      ) : (
        <>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-cloud/40">
            <ul className="divide-y divide-frost bg-white px-4" aria-busy={editing}>
              {items.map((line) => (
                <li key={line.productId} className="grid grid-cols-[4rem_minmax(0,1fr)] gap-3 py-4">
                  <Link
                    to={`/products/${line.slug}`}
                    onClick={close}
                    aria-label={`View ${line.name}`}
                    className="aspect-square self-start overflow-hidden rounded-lg border border-frost bg-white"
                  >
                    {line.image ? (
                      <OptimizedImage
                        path={line.image.path}
                        displayOrder={line.image.displayOrder}
                        alt={line.name}
                        width={line.image.width}
                        height={line.image.height}
                        size="thumbnail"
                        aspectRatio="1 / 1"
                        imageClassName="p-1.5"
                        className="h-full w-full"
                      />
                    ) : (
                      <span className="flex h-full items-center justify-center p-1 text-center text-[10px] text-neutral">
                        No image
                      </span>
                    )}
                  </Link>
                  <div className="min-w-0">
                    <Link
                      to={`/products/${line.slug}`}
                      onClick={close}
                      className="line-clamp-2 text-sm leading-snug text-navy-900 hover:text-sky-ink"
                    >
                      {line.name}
                    </Link>
                    <p className="mt-2 text-sm font-medium tabular-nums text-navy-900">
                      {formatKES(Number(line.price) * line.quantity)}
                    </p>
                    {line.quantity > 1 && (
                      <p className="mt-0.5 text-xs text-neutral">{formatKES(line.price)} each</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <div
                        role="group"
                        aria-label={`Quantity for ${line.name}`}
                        className="inline-flex items-center rounded-lg border border-frost bg-cloud/40"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            void edit(() => setQuantity(line.productId, line.quantity - 1))
                          }
                          disabled={editing || line.quantity <= 1}
                          className="flex h-11 w-11 items-center justify-center text-navy-700 disabled:opacity-30"
                          aria-label={`Decrease quantity for ${line.name}`}
                        >
                          −
                        </button>
                        <span
                          className="min-w-6 px-1 text-center text-sm tabular-nums text-navy-900"
                          aria-live="polite"
                        >
                          {line.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            void edit(() => setQuantity(line.productId, line.quantity + 1))
                          }
                          disabled={editing || line.quantity >= line.maxAvailable}
                          className="flex h-11 w-11 items-center justify-center text-navy-700 disabled:opacity-30"
                          aria-label={`Increase quantity for ${line.name}`}
                        >
                          +
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => void edit(() => remove(line.productId))}
                        disabled={editing}
                        className="flex h-11 w-11 items-center justify-center rounded-full text-neutral hover:bg-cloud hover:text-danger disabled:opacity-30"
                        aria-label={`Remove ${line.name}`}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          aria-hidden
                        >
                          <path
                            d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v5M14 11v5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                    {line.quantity >= line.maxAvailable && (
                      <p className="mt-1 text-xs text-neutral">
                        {line.maxAvailable > 0
                          ? 'Maximum available quantity reached'
                          : 'Currently unavailable'}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <CartCrossSell
              compact
              onNavigate={close}
              disabled={editing}
              onAddProduct={(product) => edit(() => add(product, 1, { silent: true }))}
            />
          </div>
          <div className="shrink-0 border-t border-frost bg-white px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <dl>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <dt className="text-sm text-neutral">Subtotal</dt>
                <dd className="text-xl font-medium tabular-nums text-navy-900">
                  {formatKES(subtotal)}
                </dd>
              </div>
            </dl>
            <p className="mt-1 text-xs leading-relaxed text-neutral">
              Choose delivery or pickup at checkout.
            </p>
            {unavailable && (
              <p className="mt-2 text-xs text-danger" role="status">
                Update unavailable quantities before checkout.
              </p>
            )}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Link
                to="/cart"
                onClick={close}
                className="flex min-h-12 items-center justify-center rounded-lg border border-frost text-sm text-navy-900 hover:border-sky-500"
              >
                View cart
              </Link>
              <Link
                to="/checkout"
                aria-disabled={blocked || undefined}
                onClick={(e) => {
                  if (blocked) e.preventDefault();
                  else close();
                }}
                className={`flex min-h-12 items-center justify-center rounded-lg bg-sky-600 text-sm font-medium text-white hover:shadow-hover ${blocked ? 'pointer-events-none opacity-50' : ''}`}
              >
                Checkout →
              </Link>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
